import React from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputAdornment
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DebounceInput } from 'react-debounce-input';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-fetching-library';

import { AlertDialogContainer } from '../others/AlertDialogContainer';
import { setToken, setGroup, setOrderFilters, setOrderHiddenColumns, setOrderColumnsWidth, setOrderColumnsSequence, setEmployeeId, setEmployeeConfigId, setUserEmail } from '../../services/redux-actions';
import { loginAction } from '../../services/request';
import { useFullScreen } from '../../services/hooks';
import Footer from '../../layouts/landing/Footer';

export const LoginPage = () => {
  const mediaWidth = useFullScreen() ? '300px' : '450px';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, mutate, error } = useMutation(loginAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');

  const validationSchema = Yup.object({
    email: Yup.string().required('To pole nie może być puste'),
    password: Yup.string()
      .required('To pole nie może być puste')
      .min(4, 'Hasło musi mieć co najmniej 4 znaki')
      .max(24, 'Hasło może mięc najwyżej 24 znaków')
  });

  const initialValues = {
    email: '',
    password: ''
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values);
    if (!error) {
      dispatch(setToken(payload.access));
      dispatch(setGroup(payload.user.groups[0]));
      dispatch(setUserEmail(payload.user.email));
      if (payload.user.groups[0] === 'clients') {
        navigate('client');
      } else {
        if (payload.user.config) {
          dispatch(setEmployeeId(payload.user.config.employee_id));
          dispatch(setEmployeeConfigId(payload.user.config.employee_config_id));
          if (payload.user.config.order_filters) {
            dispatch(setOrderFilters(payload.user.config.order_filters));
          }
          if (payload.user.config.order_hidden_columns) {
            dispatch(setOrderHiddenColumns(payload.user.config.order_hidden_columns));
          }
          if (payload.user.config.order_columns_width) {
            dispatch(setOrderColumnsWidth(payload.user.config.order_columns_width));
          }
          if (payload.user.config.order_columns_sequence) {
            dispatch(setOrderColumnsSequence(payload.user.config.order_columns_sequence));
          }
        }
        navigate('/employee');
      }
    } else {
      setSeverity('error');
      setTitle('Podano email i/lub hasło są nieprawidłowe');
      setMessage('Spróbuj ponownie jeszcze raz');
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <Box display="flex" flexDirection="column" minHeight="100%">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        component={Paper}
        elevation={4}
        padding={8}
      >
        {showAlert && (
          <AlertDialogContainer
            severity={severity}
            title={title}
            message={message}
            setShowAlert={setShowAlert}
          />
        )}
        <Box>
          <Typography color="primary" variant="h5" align="center">
          Logowanie
          </Typography>
        </Box>
        <Divider style={{ width: mediaWidth, marginTop: 20 }} />
        <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <FormControl
            style={{
              marginTop: 50,
              marginBottom: 20,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Adres e-mail
            </Typography>
            <DebounceInput
              variant="outlined"
              name="email"
              id="email"
              type="text"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AccountCircle />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <FormControl
            style={{
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Typography color="primary" variant="h6">
            Hasło
            </Typography>
            <DebounceInput
              variant="outlined"
              type="password"
              name="password"
              id="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              debounceTimeout={100}
              element={TextField}
              style={{ marginRight: 10, marginBottom: 10 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOpenIcon />
                  </InputAdornment>
                )
              }}
            />
          </FormControl>
          <Divider style={{ width: mediaWidth, marginTop: 50, marginBottom: 20 }} />
          <Button
            color="primary"
            variant="contained"
            type="submit"
            style={{ marginBottom: 20 }}
          >
          Zaloguj
          </Button>
        </form>
        <Box display="flex" flexDirection="row" justifyContent="center">
          <Button onClick={() => navigate('/registration')}>
            <Typography variant="subtitle2">Nie masz konta? <u>Zarejestruj się</u></Typography>
          </Button>
          <Divider orientation="vertical" />
          <Button onClick={() => navigate('/restore-password')}>
            <Typography variant="subtitle2">Przypomnij hasło</Typography>
          </Button>
        </Box>

        <Box display="flex" flexDirection="row" justifyContent="space-between" sx={{ mt: '20px', width: mediaWidth }}>
          <Button variant="outlined" sx={{ flex: 1 }} onClick={() => navigate('/zapisy')}>
            <Typography variant="subtitle2">Zapisy</Typography>
          </Button>
        </Box>
      </Box>
      <Box flex={1}></Box>
      <Footer />
    </ Box>
  );
};
